import Vue from 'vue'
import Sentry, { featureTags } from '@/services/sentry'
import AiUsageApi from '@/api/aiUsage'
import { aiCreditCoinKey, isResultId, toAiCreditCoin } from '@/business-logic/ai-usage/aiCreditCoin'
import { AI_USAGE_RESOURCE_TYPE, AI_USAGE_SOURCE } from '@/business-logic/ai-usage/aiUsageWire'

// Settlement can land a little after the result itself; past the last wait the coin is simply not shown.
export const AI_USAGE_LOOKUP_RETRY_DELAYS_MS = Object.freeze([1500, 3000, 6000])

const SET_COIN = 'SET_COIN'
const MARK_RESULT_SEEN = 'MARK_RESULT_SEEN'
const START_LOOKUP = 'START_LOOKUP'
const END_LOOKUP = 'END_LOOKUP'
const SET_PENDING_PROMPT_RESULT = 'SET_PENDING_PROMPT_RESULT'

const isFlagOn = (rootGetters) => Boolean(rootGetters['featureToggles/canUseAiCreditUsage'])

const SENTRY_CONTEXT = {
  featureFlag: 'AHA-48108-master-agent-ai-usage', featureName: 'ai-credit-coin', taskId: 'PRE-223',
}

const wait = (milliseconds) => new Promise((resolve) => { setTimeout(resolve, milliseconds) })

let lookupSequence = 0

const _state = {
  coins: {},
  seenResultIds: {},
  // `${source}:${presentationId}` -> { runId, sequence } of the newest completion; older answers are dropped.
  activeLookups: {},
  lookupsInFlight: {},
  // presentationId -> resultId a prompt generation returned before its final socket arrived.
  pendingPromptResults: {},
}

const _getters = {
  aiCreditCoin: (state, moduleGetters, rootState, rootGetters) => (source, presentationId) => {
    if (!isFlagOn(rootGetters)) return null
    if (!source || presentationId == null) return null
    const coin = state.coins[aiCreditCoinKey(source, presentationId)]
    return coin && coin.presentationId === presentationId ? coin : null
  },
}

const isLookupCurrent = (state, rootState, lookup) => {
  const active = state.activeLookups[lookup.key]
  return Boolean(active)
    && active.runId === lookup.runId
    && active.sequence === lookup.sequence
    && rootState.presentation?.id === lookup.presentationId
}

// A pending result may still settle, and a result id already seen belongs to an earlier run.
const isNewSettledResult = (state, settlement) => Boolean(settlement)
  && settlement.settlement === 'settled'
  && typeof settlement.resultId === 'string'
  && !state.seenResultIds[settlement.resultId]

const pollSettlement = async ({ commit, state, rootState }, lookup, attempt) => {
  const delay = attempt === 0 ? 0 : AI_USAGE_LOOKUP_RETRY_DELAYS_MS[attempt - 1]
  if (delay === undefined) return
  if (delay) await wait(delay)
  if (!isLookupCurrent(state, rootState, lookup)) return
  const settlement = await lookup.fetchSettlement()
  if (!isLookupCurrent(state, rootState, lookup)) return
  if (!isNewSettledResult(state, settlement) || !lookup.accepts(settlement)) {
    await pollSettlement({ commit, state, rootState }, lookup, attempt + 1)
    return
  }
  const coin = toAiCreditCoin(settlement)
  commit(MARK_RESULT_SEEN, settlement.resultId)
  commit(SET_COIN, { key: lookup.key, coin: coin ? { ...coin, presentationId: lookup.presentationId, runId: lookup.runId } : null })
}

const runLookup = async ({ commit, state, rootState }, {
  source, presentationId, runId, fetchSettlement, accepts = () => true,
}) => {
  const flightKey = `${source}:${runId}`
  if (state.lookupsInFlight[flightKey]) return
  const key = aiCreditCoinKey(source, presentationId)

  lookupSequence += 1
  const lookup = {
    key,
    runId,
    sequence: lookupSequence,
    presentationId,
    fetchSettlement,
    accepts,
  }
  commit(START_LOOKUP, {
    key: lookup.key, flightKey, runId, sequence: lookup.sequence,
  })
  try {
    await pollSettlement({ commit, state, rootState }, lookup, 0)
  } catch (error) {
    Sentry.captureException(error, { tags: featureTags(SENTRY_CONTEXT) })
  } finally {
    commit(END_LOOKUP, flightKey)
  }
}

const lookupResultById = (context, { source, presentationId, resultId }) => runLookup(context, {
  source,
  presentationId,
  runId: resultId,
  fetchSettlement: () => AiUsageApi.lookupById(resultId),
  accepts: (settlement) => settlement.resultId === resultId,
})

const promptResultIdOf = (aiUsage) => (isResultId(aiUsage?.resultId) ? aiUsage.resultId : null)

const actions = {
  /**
   * Look up the server settlement of a finished import job by deck and job id, and show its coin.
   * A newer completion on the same deck, or leaving the deck, drops the answer.
   */
  async lookupResultSettlementAction(context, { source, presentationId, jobId }) {
    if (!isFlagOn(context.rootGetters)) return
    if (!source || presentationId == null || jobId == null) return
    await runLookup(context, {
      source,
      presentationId,
      runId: jobId,
      fetchSettlement: () => AiUsageApi.getSettledResult({
        resourceType: AI_USAGE_RESOURCE_TYPE.PRESENTATION,
        resourceId: presentationId,
        jobId,
      }),
    })
  },

  /**
   * Take the `aiUsage` a prompt generation returned on its HTTP response. Its settlement is looked up once
   * the generation has finished: now when `finished`, else on `finishPromptResultAction`.
   */
  async trackPromptResultAction(context, { presentationId, aiUsage, finished }) {
    if (!isFlagOn(context.rootGetters) || presentationId == null) return
    const resultId = promptResultIdOf(aiUsage)
    if (!resultId) {
      context.commit(SET_PENDING_PROMPT_RESULT, { presentationId, resultId: null })
      return
    }
    if (!finished) {
      context.commit(SET_PENDING_PROMPT_RESULT, { presentationId, resultId })
      return
    }
    await lookupResultById(context, { source: AI_USAGE_SOURCE.PRESENTATION_PROMPT, presentationId, resultId })
  },

  /** The prompt generation on this deck finished: look up the result its response returned, if any. */
  async finishPromptResultAction(context, { presentationId }) {
    if (!isFlagOn(context.rootGetters) || presentationId == null) return
    const resultId = context.state.pendingPromptResults[presentationId]
    if (!resultId) return
    context.commit(SET_PENDING_PROMPT_RESULT, { presentationId, resultId: null })
    await lookupResultById(context, { source: AI_USAGE_SOURCE.PRESENTATION_PROMPT, presentationId, resultId })
  },
}

const mutations = {
  [SET_COIN](state, { key, coin }) {
    if (coin) {
      Vue.set(state.coins, key, coin)
    } else {
      Vue.delete(state.coins, key)
    }
  },
  [MARK_RESULT_SEEN](state, resultId) {
    Vue.set(state.seenResultIds, resultId, true)
  },
  [START_LOOKUP](state, {
    key, flightKey, runId, sequence,
  }) {
    Vue.set(state.activeLookups, key, { runId, sequence })
    Vue.set(state.lookupsInFlight, flightKey, true)
    // A replayed completion of the run already shown re-checks it without hiding its coin.
    if (state.coins[key]?.runId !== runId) Vue.delete(state.coins, key)
  },
  [END_LOOKUP](state, flightKey) {
    Vue.delete(state.lookupsInFlight, flightKey)
  },
  [SET_PENDING_PROMPT_RESULT](state, { presentationId, resultId }) {
    if (resultId) {
      Vue.set(state.pendingPromptResults, presentationId, resultId)
    } else {
      Vue.delete(state.pendingPromptResults, presentationId)
    }
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
